import PropTypes from 'prop-types';
import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Card, CardBody, Col, Row } from 'reactstrap';
import { useAuthProvider } from 'utils/api/useAuthProvider';
import ElasticsearchAPI from '../../utils/api/ElasticsearchAPI';
import PageError from '../Layout/Pages/Errors/PageError';
import Loading from '../Widgets/Loading';
import FilterInput from '../Widgets/Search/FilterInput';
import CredentialList from './CredentialList';

const CredentialsDirectory: FC<Props> = (props) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  const [credentials, setCredentials] = useState(undefined);
  const [errorMessage, setErrorMessage] = useState(null);
  const [filterName, setFilterName] = useState('');

  const { user } = useAuthProvider();
  const userSub = user?.sub;
  // @ts-expect-error
  const currentOrgId = props.currentOrganization?.id;

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    const MAX_CREDENTIALS_TO_SHOW = 9999;

    if (userSub && currentOrgId) {
      const queryParams = {
        size: MAX_CREDENTIALS_TO_SHOW,
        sort: [
          {
            'name.raw': 'asc',
          },
        ],
      };

      ElasticsearchAPI.search(
        // only cache if no query provided (so we don't take up too much
        // memory story search results as searches are done)
        filterName ? undefined : userSub,
        // @ts-expect-error
        props.currentProxyPerson,
        currentOrgId,
        'get-credentials-by-name',
        filterName
          ? {
              ...queryParams,
              name: filterName,
            }
          : queryParams,
        (hits) => {
          const newCredentials = hits.map((h) => h._source);
          if (isMounted) {
            setCredentials(newCredentials);
          }
        },
        (message) => {
          setErrorMessage(message);
        }
      );
    }
    // @ts-expect-error
  }, [isMounted, userSub, filterName, currentOrgId, props.currentProxyPerson]);

  if (errorMessage) {
    console.error('Could not load all credentials: ' + errorMessage);
    return <PageError message={errorMessage} />;
  }

  if (typeof credentials === 'undefined') {
    return <Loading />;
  }

  const onChangeFilter = (e) => {
    setFilterName(e.target.value);
  };

  const credentialsContent = (
    <>
      {/* @ts-expect-error */}
      {credentials.length === 0 && (
        <div className="text-center mb-4">
          <span className="text-muted">
            <FormattedMessage
              id="app.views.credentials.credentials_directory.no_results.text"
              defaultMessage="No results found"
            />
          </span>
        </div>
      )}
      <CredentialList credentials={credentials} />
    </>
  );

  return (
    <Row className="justify-content-md-center">
      {/*<Col md={4} className="order-1 order-md-2">
        {!props.inModal && (
          <CredentialListCard
            title="Recommended credentials"
            loaderFunction={(p, c, e) =>
              ElasticsearchAPI.getTopCredentials(userSub, currentOrgId, p, c, e)
            }
            hideDescriptions={true}
            emptyState="You have no recommended teams."
          />
        )}
      </Col>*/}
      <Col md="12" className="order-2 order-md-1">
        <FilterInput
          value={filterName}
          onChange={onChangeFilter}
          // @ts-expect-error
          autoFocus={props.inModal}
        />
        {/* @ts-expect-error */}
        {props.inModal && <div className="mt-4">{credentialsContent}</div>}
        {/* @ts-expect-error */}
        {!props.inModal && (
          <Card className="mt-4 mb-0">
            <CardBody>{credentialsContent}</CardBody>
          </Card>
        )}
      </Col>
    </Row>
  );
};

const CredentialsDirectory_propTypes = {
  currentOrganization: PropTypes.object.isRequired,
};

type Props = PropTypes.InferProps<typeof CredentialsDirectory_propTypes>;

const mapStateToProps = (state) => {
  const { currentOrganization, currentProxyPerson } = state;

  return {
    currentOrganization,
    currentProxyPerson,
  };
};

// all tracking in app will be passed through here
export default connect(mapStateToProps)(React.memo(CredentialsDirectory));
